import Add from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UnfoldLess from "@mui/icons-material/UnfoldLess";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { amber, red } from "@mui/material/colors";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useLazyQuery, useQuery, useSuspenseQuery } from "@apollo/client";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { SubmissionReviewerCell } from "components/Submission/SubmissionReviewerCell";
import { gql } from "generated-graphql";
import {
  RegulatoryStatusOption,
  SignatureType,
  SubmissionStatus,
  SubmissionVirtualEventsQuery,
} from "generated-graphql/graphql";
import { useAutoFlex } from "hooks/useAutoFlex";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import {
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { SUBMISSION_VIRTUAL_EVENTS_QUERY } from "queries/submissions";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SubmissionBulkActionsMenu } from "./SubmissionBulkActionsMenu";
import { SubmissionsEmptyState } from "./SubmissionsEmptyState";
import { SubmissionStatsOverview } from "./Overview";
import {
  WasteSubmissionStatsProvider,
  useWasteSubmissionStats,
} from "./StatsContext";

export type Row =
  SubmissionVirtualEventsQuery["submissionVirtualEvents"]["items"][0];

export const STATUS_LABELS: Record<string, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

// Status filter options for multi-select checkbox menu
const STATUS_FILTER_OPTIONS: Record<SubmissionStatus, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

// US States list for filtering
const US_STATES = Object.keys(STATE_NAMES);

// Generator status filter options
const GENERATOR_STATUS_OPTIONS: {
  label: string;
  value: RegulatoryStatusOption;
}[] = [
  { label: "LQG", value: RegulatoryStatusOption.LargeQuantityGenerator },
  { label: "SQG", value: RegulatoryStatusOption.SmallQuantityGenerator },
  { label: "VSQG", value: RegulatoryStatusOption.VerySmallQuantityGenerator },
  { label: "None", value: RegulatoryStatusOption.None },
];

const ALL_GENERATOR_STATUSES = [
  RegulatoryStatusOption.LargeQuantityGenerator,
  RegulatoryStatusOption.SmallQuantityGenerator,
  RegulatoryStatusOption.VerySmallQuantityGenerator,
  RegulatoryStatusOption.None,
];

// Tags query for filtering
const FACILITY_TAGS_QUERY = gql(`
  query SubmissionsFacilityTags(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tags(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

// Metrics query for filter bar chips
const SUBMISSIONS_METRICS_QUERY = gql(`
  query SubmissionsMetrics($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      total
      overdue
      readyForAction
    }
  }
`);

// Date picker shortcuts
const datePickerShortcuts: PickersShortcutsProps<DateRange<DateTime>>["items"] =
  [
    {
      label: "This Year",
      getValue: () => [
        DateTime.now().startOf("year"),
        DateTime.now().endOf("year"),
      ],
    },
    {
      label: "Last Year",
      getValue: () => [
        DateTime.now().minus({ years: 1 }).startOf("year"),
        DateTime.now().minus({ years: 1 }).endOf("year"),
      ],
    },
    {
      label: "Reset",
      getValue: () => [null, null],
    },
  ];

// Default = All statuses except completed (Filing Complete, Not Reporting)
const DEFAULT_STATUSES = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
];

const ALL_STATUSES = Object.keys(STATUS_FILTER_OPTIONS) as SubmissionStatus[];

// Default date filter = current year
const DEFAULT_DUE_DATE_START = DateTime.now().startOf("year").toISODate();
const DEFAULT_DUE_DATE_END = DateTime.now().endOf("year").toISODate();
const DEFAULT_DUE_DATE = `${DEFAULT_DUE_DATE_START}..${DEFAULT_DUE_DATE_END}`;

const getStatusLabel = (status: string): string => {
  return STATUS_LABELS[status] || status;
};

// Helper to extract a key's value from an omnisearch string
function extractOmnisearchValue(
  omnisearch: string | null,
  key: string
): string | undefined {
  if (!omnisearch) return undefined;
  const regex = new RegExp(`(?:^|\\s)${key}:(?:"([^"]+)"|([^\\s]+))`, "i");
  const match = omnisearch.match(regex);
  if (match) {
    return match[1] ?? match[2];
  }
  return undefined;
}

// Helper to compare arrays (order-independent)
function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

// Metric chip component for filter bar - toggle chip for ready for action
function MetricChips({
  filter,
  isReadyActive,
  onReadyToggle,
}: {
  filter?: {
    dueDateStart?: string;
    dueDateEnd?: string;
    states?: string[];
    statuses?: SubmissionStatus[];
    generatorStatuses?: RegulatoryStatusOption[];
    facilityTagNames?: string[];
  };
  isReadyActive: boolean;
  onReadyToggle: () => void;
}) {
  const { tenantId } = useTenant();

  const { data } = useSuspenseQuery(SUBMISSIONS_METRICS_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: filter ?? undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const summary = data?.wasteSubmissionsSummary;
  const readyForAction = summary?.readyForAction ?? 0;

  return (
    <Chip
      label={`Ready: ${readyForAction}`}
      size="small"
      variant={isReadyActive ? "filled" : "outlined"}
      color={isReadyActive || readyForAction > 0 ? "primary" : "default"}
      onClick={onReadyToggle}
      onDelete={isReadyActive ? onReadyToggle : undefined}
      sx={{ cursor: "pointer" }}
    />
  );
}

// Note: This component is wrapped with OmnisearchProvider in main.tsx router
export function WasteSubmissions() {
  return (
    <WasteSubmissionStatsProvider>
      <WasteSubmissionsContent />
    </WasteSubmissionStatsProvider>
  );
}

function WasteSubmissionsContent() {
  const { tenantId: tenantIdFromParams } = useParams();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { isStaff, user } = useCurrentUser();
  const currentUserEmail = user?.email;
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { statsOpen, handleToggle } = useWasteSubmissionStats();
  const [selectedChartStatus, setSelectedChartStatus] =
    useState<SubmissionStatus | null>(null);
  const [selectedChartState, setSelectedChartState] = useState<string | null>(
    null
  );
  const [selectedChartGeneratorStatus, setSelectedChartGeneratorStatus] =
    useState<string | null>(null);

  // Generator status filter state
  const [generatorStatusOpen, setGeneratorStatusOpen] = useState(false);

  // Tags filter state
  const [tagsMenuAnchor, setTagsMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [facilityTagsExpanded, setFacilityTagsExpanded] = useState(true);
  const [allFacilityTags, setAllFacilityTags] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [facilityTagsPage, setFacilityTagsPage] = useState(0);
  const [isFetchingMoreFacilityTags, setIsFetchingMoreFacilityTags] =
    useState(false);

  // Fetch facility tags - use regular useQuery to avoid suspending the whole component
  const { data: tagsData } = useQuery(FACILITY_TAGS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} type:Facility`,
      page: 0,
      pageSize: 100,
      sort: [{ field: "name", sort: "asc" }],
    },
    skip: !tenantId,
  });

  // Lazy query for loading more tags
  const [fetchMoreFacilityTags] = useLazyQuery(FACILITY_TAGS_QUERY, {
    onCompleted: (data) => {
      if (data?.tags?.items) {
        setAllFacilityTags((prev) => [...prev, ...data.tags.items]);
      }
      setIsFetchingMoreFacilityTags(false);
    },
    onError: () => {
      setIsFetchingMoreFacilityTags(false);
    },
  });

  // Initialize tags list with initial data
  useEffect(() => {
    if (tagsData?.tags?.items) {
      setAllFacilityTags(tagsData.tags.items);
    }
  }, [tagsData]);

  // Reset tags when menu opens
  useEffect(() => {
    if (tagsMenuAnchor && tagsData?.tags?.items) {
      setAllFacilityTags(tagsData.tags.items);
      setFacilityTagsPage(0);
    }
  }, [tagsMenuAnchor, tagsData]);

  const hasMoreFacilityTags = useMemo(() => {
    if (!tagsData?.tags?.count) return false;
    return allFacilityTags.length < tagsData.tags.count;
  }, [tagsData?.tags?.count, allFacilityTags.length]);

  const handleFacilityTagsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrollThreshold = 50;
      const scrollDistance =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (
        scrollDistance < scrollThreshold &&
        hasMoreFacilityTags &&
        !isFetchingMoreFacilityTags
      ) {
        setIsFetchingMoreFacilityTags(true);
        const nextPage = facilityTagsPage + 1;
        setFacilityTagsPage(nextPage);

        fetchMoreFacilityTags({
          variables: {
            search: `tenantId:${tenantId} type:Facility`,
            page: nextPage,
            pageSize: 100,
            sort: [{ field: "name", sort: "asc" }],
          },
        });
      }
    },
    [
      hasMoreFacilityTags,
      isFetchingMoreFacilityTags,
      facilityTagsPage,
      tenantId,
      fetchMoreFacilityTags,
    ]
  );

  const handleRowClick = useCallback(
    (row: Row) => {
      const submissionId = row.submission?.id;
      if (!isStaff) {
        navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
        return;
      }

      // Staff users: check submission status
      const isVirtualSubmission =
        row.submission?.status === SubmissionStatus.NotStarted;

      if (isVirtualSubmission) {
        // Navigate to create submission with taskId and startDate parameters
        const dateOnly = DateTime.fromISO(String(row.deadline)).toISODate();
        navigate(
          `/o/${tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`
        );
      } else {
        // Determine if submission should be viewed in read-only mode
        const status = row.submission?.status;
        const shouldViewReadOnly =
          status === SubmissionStatus.EncampInitialReview ||
          status === SubmissionStatus.InReview ||
          status === SubmissionStatus.FilingComplete ||
          status === SubmissionStatus.NotReporting;

        if (shouldViewReadOnly) {
          // Navigate to read-only view mode for:
          // - ENCAMP_INITIAL_REVIEW: Staff reviewer evaluates before sending to customers
          // - IN_REVIEW: Customers are actively reviewing/signing - staff should not be editing
          // - FILING_COMPLETE: Terminal state - nothing left to edit
          // - NOT_REPORTING: Opt-out status - rarely needs changes
          navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
        } else {
          // Navigate to edit mode for:
          // - IN_PROGRESS: Staff actively works on draft
          // - PROCESSING_SUBMISSION: Staff is filing - may need to update metadata
          navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`);
        }
      }
    },
    [isStaff, navigate, tenantId]
  );

  const handleAddSubmission = useCallback(() => {
    navigate(`/o/${tenantId}/waste/submissions/create`);
  }, [navigate, tenantId]);

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions" },
  ]);

  const omnisearchPersistenceKey = "submissions-list-view-v9";
  const autoFlex = useAutoFlex(
    1.0,
    {
      deadline: 0.1,
      title: 0.25,
      facilities: 0.1,
      "submission.status": 0.1,
      submissionType: 0.1,
      "submission.title": 0.1,
    },
    [
      "facilities",
      "submissionType",
      "title",
      "documents",
      "reviewers",
      "signer",
      "staffAssignee",
      "submission.status",
      "submission.title",
    ]
  );

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "deadline",
      headerName: "Due Date",
      sortable: true,
      flex: autoFlex.get("deadline"),
      minWidth: 20,
      renderCell: ({ value }) => {
        return (
          <CellText variant="body2">
            {DateTime.fromISO(value).toFormat("MM/dd/yyyy")}
          </CellText>
        );
      },
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: autoFlex.get("facilities"),
      minWidth: 20,
      sortable: true,
      renderCell({ row }) {
        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {(row.facilities ?? []).map((facility) => (
              <Typography
                key={facility.id}
                variant="body2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={facility.name}>
                  <span>{facility.name}</span>
                </Tooltip>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "title",
      headerName: "Submission Type",
      flex: autoFlex.get("title"),
      minWidth: 100,
      sortable: true,
      renderCell: ({ row: { title } }) => {
        return (
          <CellText variant="body2" noWrap>
            {/* For SYSTEM submissions, this is the pretty printed submission type
            For user submissions, this is the task title which is meant to be in sync with the submission title*/}
            {title}
          </CellText>
        );
      },
    },
    {
      field: "submission.title",
      headerName: "Submission Title",
      flex: autoFlex.get("submission.title"),
      minWidth: 100,
      sortable: true,
      renderCell: ({ row: { submission } }) => {
        return (
          <CellText variant="body2" noWrap>
            {submission?.title}
          </CellText>
        );
      },
    },

    {
      field: "notes",
      headerName: "Notes",
      flex: autoFlex.get("notes"),
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => {
        const notes = row.submission?.notes;
        if (!notes) return "";
        return (
          <CellText variant="body2" noWrap>
            {notes}
          </CellText>
        );
      },
    },
    {
      field: "documents",
      headerName: "Attachment(s)",
      flex: autoFlex.get("documents"),
      minWidth: 60,
      sortable: false,
      renderCell: (params) => {
        const documents = params.row.submission?.documents || [];

        if (documents.length === 0) return "";
        const mappedDocuments = documents
          .map((doc) => doc.document.title)
          .join(", ");
        return (
          <Tooltip title={mappedDocuments}>
            <CellText variant="body2">{mappedDocuments}</CellText>
          </Tooltip>
        );
      },
    },
    {
      field: "reviewers",
      headerName: "Reviewer(s)",
      flex: autoFlex.get("reviewers"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const reviewers = params.row.submission?.reviewers || [];
        const reviewMode = params.row.submission?.reviewMode;

        if (reviewers.length === 0) {
          return null;
        }

        return (
          <SubmissionReviewerCell
            reviewers={reviewers}
            reviewMode={reviewMode}
            isStaff={isStaff}
            type="reviewer"
          />
        );
      },
    },
    {
      field: "signer",
      headerName: "Signer",
      flex: autoFlex.get("signer"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const reviewers = params.row.submission?.reviewers || [];
        const reviewMode = params.row.submission?.reviewMode;
        const signers = reviewers.filter((r) => r.isSigner);
        const signatureType = params.row.submission?.signatureType;

        // Handle no signature required cases
        if (signatureType === SignatureType.None || signers.length === 0) {
          return (
            <CellText variant="body2" noWrap>
              No Signature Required
            </CellText>
          );
        }

        return (
          <SubmissionReviewerCell
            reviewers={reviewers}
            reviewMode={reviewMode}
            isStaff={isStaff}
            type="signer"
          />
        );
      },
    },
    {
      field: "staffAssignee",
      headerName: "Encamp Assignee",
      flex: autoFlex.get("staffAssignee"),
      minWidth: 90,
      renderCell: ({ row }) => {
        if (!row?.submission?.staffAssignee) {
          return null;
        }
        const person = row.submission.staffAssignee.person;
        if (!person) {
          return (
            <CellText variant="body2" noWrap>
              {row.submission.staffAssignee.email}
            </CellText>
          );
        }
        const cellText =
          `${person.first || ""} ${person.last || ""}`.trim() ||
          row.submission.staffAssignee.email;
        return (
          <CellText variant="body2" noWrap>
            {cellText}
          </CellText>
        );
      },
    },
    {
      field: "submission.status",
      headerName: "Status",
      flex: autoFlex.get("submission.status"),
      minWidth: 120,
      sortable: true,
      renderCell({ row: { submission } }) {
        return (
          <CellText variant="body2" noWrap>
            {getStatusLabel(submission?.status ?? SubmissionStatus.NotStarted)}
          </CellText>
        );
      },
    },
    {
      field: "waitingOn",
      headerName: "Waiting On",
      flex: autoFlex.get("waitingOn"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const waitingOn = params.row.waitingOn || [];

        if (waitingOn.length === 0) {
          return null;
        }

        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {waitingOn.map((user) => {
              const displayName =
                user.person?.first || user.person?.last
                  ? `${user.person.first || ""} ${
                      user.person.last || ""
                    }`.trim()
                  : user.email;

              return (
                <Typography
                  key={user.id || user.email}
                  variant="body2"
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tooltip title={user.email}>
                    <span>{displayName}</span>
                  </Tooltip>
                </Typography>
              );
            })}
          </Stack>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      sortable: false,
      align: "right",
      getActions: ({ row }) => {
        const actions: any[] = [];

        if (row.submission?.status === SubmissionStatus.NotStarted) {
          actions.push(
            <Tooltip title="Create Submission" key="createSubmission">
              <GridActionsCellItem
                onClick={() => handleRowClick(row)}
                label="Create Submission"
                icon={<Add />}
              />
            </Tooltip>
          );
        }

        return actions;
      },
    },
  ];

  const {
    omnisearch,
    setOmnisearch,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
    columns: omnisearchColumns,
  } = useOmnisearchDatagrid({
    persistenceKey: omnisearchPersistenceKey,
    isURLDriven: true,
    columns,
    initialSortModel: [
      { field: "dueDate", sort: "asc" },
      { field: "title", sort: "asc" },
    ],
    initialPageSize: 50,
  });

  // Facility tags filter state - derived from omnisearch (syncs to grid query)
  // Uses tag NAMES for search (like facilityTags:"California Sites")
  const facilityTagsFilterValue = extractOmnisearchValue(
    omnisearch,
    "facilityTags"
  );
  const selectedFacilityTagNames = useMemo(() => {
    if (!facilityTagsFilterValue) return [];
    return facilityTagsFilterValue.split(/[|,]/).filter((s) => s.length > 0);
  }, [facilityTagsFilterValue]);

  const handleReadyForActionChange = useCallback(
    (checked: boolean) => {
      if (checked && currentUserEmail) {
        editOmnisearchKeyValues([["readyForAction", currentUserEmail]]);
      } else {
        removeOmnisearchKeyValue("readyForAction");
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, currentUserEmail]
  );

  // Status filter state - derived from omnisearch hook state
  const [statusOpen, setStatusOpen] = useState(false);
  const statusFilterValue = extractOmnisearchValue(
    omnisearch,
    "submissionStatus"
  );
  const selectedStatuses = useMemo(() => {
    // No filter in omnisearch = use default statuses (excludes completed)
    if (statusFilterValue === undefined || statusFilterValue === "") {
      return [...DEFAULT_STATUSES];
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    return statusFilterValue.split(/[|,]/).filter((s) => s.length > 0);
  }, [statusFilterValue]);

  // Status filter handler for individual status selections
  const handleStatusesChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const raw = e.target.value as string[];
      // Filter out "all" - it's handled by onClick
      const values = raw.filter((v) => v !== "all");

      if (values.length === 0) {
        // Can't deselect all - revert to default (remove from omnisearch)
        removeOmnisearchKeyValue("submissionStatus");
      } else if (arraysEqual(values, DEFAULT_STATUSES)) {
        // Selecting exactly defaults - remove from omnisearch (let dynamic default handle it)
        removeOmnisearchKeyValue("submissionStatus");
      } else {
        // Any other selection (including ALL) - add to omnisearch
        editOmnisearchKeyValues([["submissionStatus", values.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Toggle all statuses handler
  const handleToggleAllStatuses = useCallback(() => {
    const isAllSelected = selectedStatuses.length === ALL_STATUSES.length;
    if (isAllSelected) {
      // Toggle from all → default (remove from omnisearch to use dynamic default)
      removeOmnisearchKeyValue("submissionStatus");
    } else {
      // Select all - must add to omnisearch to override default
      editOmnisearchKeyValues([["submissionStatus", ALL_STATUSES.join("|")]]);
    }
  }, [
    selectedStatuses.length,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  ]);

  const readyForAction = !!extractOmnisearchValue(omnisearch, "readyForAction");

  // Static defaultSearch - always includes defaults
  // The omnisearch backend merges duplicate keys, with later values taking precedence
  // So if user sets submissionStatus:X in omnisearch, it overrides the default here
  const defaultSearch = useMemo(
    () =>
      `tenantId:${tenantId} status:ACTIVE|OVERDUE submissionStatus:${DEFAULT_STATUSES.join("|")} dueDate:${DEFAULT_DUE_DATE}`,
    [tenantId]
  );

  const handleViewUpcoming = useCallback(() => {
    editOmnisearchKeyValues([
      ["submissionStatus", SubmissionStatus.NotStarted],
    ]);
  }, [editOmnisearchKeyValues]);

  const clearOmnisearch = useCallback(() => {
    setOmnisearch("");
  }, [setOmnisearch]);

  // Handler for clicking on a status in the chart
  const handleChartStatusClick = useCallback(
    (status: SubmissionStatus) => {
      // If clicking the same status, clear the filter
      if (selectedChartStatus === status) {
        setSelectedChartStatus(null);
        // Reset to default statuses (remove from omnisearch)
        removeOmnisearchKeyValue("submissionStatus");
      } else {
        setSelectedChartStatus(status);
        editOmnisearchKeyValues([["submissionStatus", status]]);
      }
    },
    [selectedChartStatus, editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Handler for clicking on a state in the chart
  const handleChartStateClick = useCallback(
    (state: string) => {
      // If clicking the same state, clear the filter
      if (selectedChartState === state) {
        setSelectedChartState(null);
        removeOmnisearchKeyValue("states");
      } else {
        setSelectedChartState(state);
        editOmnisearchKeyValues([["states", state]]);
      }
    },
    [selectedChartState, editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Handler for clicking on a generator status in the chart
  const handleChartGeneratorStatusClick = useCallback(
    (status: string) => {
      // If clicking the same status, clear the filter
      if (selectedChartGeneratorStatus === status) {
        setSelectedChartGeneratorStatus(null);
        removeOmnisearchKeyValue("generatorStatus");
      } else {
        setSelectedChartGeneratorStatus(status);
        editOmnisearchKeyValues([["generatorStatus", status]]);
      }
    },
    [
      selectedChartGeneratorStatus,
      editOmnisearchKeyValues,
      removeOmnisearchKeyValue,
    ]
  );

  // Handler for toggling "Ready for Action" filter
  // Only toggles readyForAction - does NOT change status filter
  const handleReadyForActionToggle = useCallback(() => {
    if (readyForAction) {
      // Turn off ready for action filter
      removeOmnisearchKeyValue("readyForAction");
    } else if (currentUserEmail) {
      // Turn on ready for action filter
      editOmnisearchKeyValues([["readyForAction", currentUserEmail]]);
    }
    setSelectedChartStatus(null);
  }, [
    readyForAction,
    currentUserEmail,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  ]);

  // Handler for resetting all filters to default
  const handleResetFilters = useCallback(() => {
    // Clear all filters from omnisearch at once - using setOmnisearch directly
    // to avoid stale state issues with multiple removeOmnisearchKeyValue calls
    const keysToRemove = new Set([
      "submissionStatus",
      "dueDate",
      "states",
      "generatorStatus",
      "readyForAction",
      "facilityTags",
    ]);

    // Parse omnisearch tokens respecting quotes
    const tokens: string[] = [];
    let current = "";
    let inQuotes = false;
    let quoteChar = "";
    for (let i = 0; i < omnisearch.length; i++) {
      const char = omnisearch[i];
      if (!inQuotes && (char === '"' || char === "'")) {
        inQuotes = true;
        quoteChar = char;
        current += char;
      } else if (inQuotes && char === quoteChar) {
        inQuotes = false;
        quoteChar = "";
        current += char;
      } else if (!inQuotes && char === " ") {
        if (current.trim()) {
          tokens.push(current.trim());
          current = "";
        }
      } else {
        current += char;
      }
    }
    if (current.trim()) tokens.push(current.trim());

    // Filter out tokens with keys to remove
    const filteredTokens = tokens.filter((token) => {
      const colonIndex = token.indexOf(":");
      if (colonIndex > 0) {
        const key = token.substring(0, colonIndex);
        return !keysToRemove.has(key);
      }
      return true; // Keep non-key-value tokens
    });

    setOmnisearch(filteredTokens.join(" "));

    // Reset chart selections
    setSelectedChartStatus(null);
    setSelectedChartState(null);
    setSelectedChartGeneratorStatus(null);
  }, [omnisearch, setOmnisearch]);

  // Derive timeFilter equivalent for empty state component
  const timeFilterForEmptyState = useMemo(() => {
    if (selectedStatuses.length === 1) {
      if (selectedStatuses[0] === SubmissionStatus.NotStarted)
        return "upcoming";
      if (
        selectedStatuses[0] === SubmissionStatus.FilingComplete ||
        selectedStatuses[0] === SubmissionStatus.NotReporting
      )
        return "completed";
    }
    // Check if it's the "in-progress" group
    const inProgressStatuses = [
      SubmissionStatus.InProgress,
      SubmissionStatus.EncampInitialReview,
      SubmissionStatus.InReview,
      SubmissionStatus.ProcessingSubmission,
    ];
    if (
      selectedStatuses.length > 0 &&
      selectedStatuses.every((s) =>
        inProgressStatuses.includes(s as SubmissionStatus)
      )
    ) {
      return "in-progress";
    }
    return "";
  }, [selectedStatuses]);

  // State filter state - derived from omnisearch hook state
  // Default: all states selected. Deselect all = no states (no results)
  const [stateOpen, setStateOpen] = useState(false);
  const statesFilterValue = extractOmnisearchValue(omnisearch, "states");

  const selectedStates = useMemo(() => {
    // No filter = all states selected
    if (statesFilterValue === undefined || statesFilterValue === "") {
      return [...US_STATES];
    }
    // "none" marker = no states selected
    if (statesFilterValue === "none") {
      return [];
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    return statesFilterValue.split(/[|,]/).filter((s) => s.length > 0);
  }, [statesFilterValue]);

  const handleStatesChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const raw = e.target.value as string[];
      const values = raw.filter((v) => v !== "select-all");

      if (values.length === 0) {
        // None selected = set "none" marker
        editOmnisearchKeyValues([["states", "none"]]);
      } else if (values.length === US_STATES.length) {
        // All selected = remove filter
        removeOmnisearchKeyValue("states");
      } else {
        // Specific states selected = apply filter
        editOmnisearchKeyValues([["states", values.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  const handleToggleAllStates = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (selectedStates.length === US_STATES.length) {
        // All selected -> deselect all
        editOmnisearchKeyValues([["states", "none"]]);
      } else {
        // Not all selected -> select all
        removeOmnisearchKeyValue("states");
      }
    },
    [selectedStates.length, removeOmnisearchKeyValue, editOmnisearchKeyValues]
  );

  // Generator status filter state - derived from omnisearch
  const generatorStatusFilterValue = extractOmnisearchValue(
    omnisearch,
    "generatorStatus"
  );
  const selectedGeneratorStatuses = useMemo(() => {
    if (
      generatorStatusFilterValue === undefined ||
      generatorStatusFilterValue === ""
    ) {
      return [...ALL_GENERATOR_STATUSES];
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    return generatorStatusFilterValue
      .split(/[|,]/)
      .filter((s) => s.length > 0) as RegulatoryStatusOption[];
  }, [generatorStatusFilterValue]);

  const handleGeneratorStatusChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const raw = e.target.value as string[];
      const values = raw.filter((v) => v !== "select-all");

      if (
        values.length === 0 ||
        values.length === ALL_GENERATOR_STATUSES.length
      ) {
        removeOmnisearchKeyValue("generatorStatus");
      } else {
        editOmnisearchKeyValues([["generatorStatus", values.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  const handleToggleAllGeneratorStatuses = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      removeOmnisearchKeyValue("generatorStatus");
    },
    [removeOmnisearchKeyValue]
  );

  // Date range filter state - derived from omnisearch hook state
  const dueDateValue = extractOmnisearchValue(omnisearch, "dueDate");
  const [dueDateStart, dueDateEnd] = useMemo(() => {
    // No filter in omnisearch = use default (current year)
    if (!dueDateValue) {
      return [
        DateTime.fromISO(DEFAULT_DUE_DATE_START),
        DateTime.fromISO(DEFAULT_DUE_DATE_END),
      ];
    }
    const parts = dueDateValue.split("..");
    const start = parts[0] ? DateTime.fromISO(parts[0]) : null;
    const end = parts[1] ? DateTime.fromISO(parts[1]) : null;
    return [start, end];
  }, [dueDateValue]);

  // Build filter for stats query based on current omnisearch filters
  const statsFilter = useMemo(() => {
    const filter: {
      dueDateStart?: string;
      dueDateEnd?: string;
      states?: string[];
      statuses?: SubmissionStatus[];
      generatorStatuses?: RegulatoryStatusOption[];
      facilityTagNames?: string[];
    } = {};

    if (dueDateStart?.isValid) {
      filter.dueDateStart = dueDateStart.toISO() ?? undefined;
    }
    if (dueDateEnd?.isValid) {
      filter.dueDateEnd = dueDateEnd.toISO() ?? undefined;
    }
    if (selectedStates.length > 0 && selectedStates.length < US_STATES.length) {
      filter.states = selectedStates;
    }
    // Include status filter so counts respect the selected statuses
    if (
      selectedStatuses.length > 0 &&
      selectedStatuses.length < ALL_STATUSES.length
    ) {
      filter.statuses = selectedStatuses as SubmissionStatus[];
    }
    if (
      selectedGeneratorStatuses.length > 0 &&
      selectedGeneratorStatuses.length < ALL_GENERATOR_STATUSES.length
    ) {
      filter.generatorStatuses = selectedGeneratorStatuses;
    }
    if (selectedFacilityTagNames.length > 0) {
      filter.facilityTagNames = selectedFacilityTagNames;
    }

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [
    dueDateStart,
    dueDateEnd,
    selectedStates,
    selectedStatuses,
    selectedGeneratorStatuses,
    selectedFacilityTagNames,
  ]);

  const handleDueDateRangeChange = useCallback(
    (newValue: DateRange<DateTime>) => {
      const [start, end] = newValue;
      if (!start && !end) {
        // Cleared - remove from omnisearch (dynamic default will apply)
        removeOmnisearchKeyValue("dueDate");
      } else {
        const startStr = start?.toISODate() ?? "";
        const endStr = end?.toISODate() ?? "";
        const newDateRange = `${startStr}..${endStr}`;
        // If selecting exactly default date range, remove from omnisearch
        if (newDateRange === DEFAULT_DUE_DATE) {
          removeOmnisearchKeyValue("dueDate");
        } else {
          editOmnisearchKeyValues([["dueDate", newDateRange]]);
        }
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Clear bulk selections when filtering changes
  useEffect(() => {
    setSelectedRows([]);
    setSelectedIds([]);
  }, [omnisearch]);

  // Simple static title - filter context is shown in the Status dropdown
  const titleFilter = "Submissions";

  // Custom empty state overlay for the grid
  const CustomNoRowsOverlay = useCallback(
    () => (
      <SubmissionsEmptyState
        timeFilter={timeFilterForEmptyState}
        readyForAction={readyForAction}
        isStaff={isStaff}
        onCreateSubmission={handleAddSubmission}
        onViewUpcoming={handleViewUpcoming}
        omnisearch={omnisearch}
        onClearSearch={clearOmnisearch}
      />
    ),
    [
      timeFilterForEmptyState,
      readyForAction,
      isStaff,
      handleAddSubmission,
      handleViewUpcoming,
      omnisearch,
      clearOmnisearch,
    ]
  );

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Submission Info",
        fields: [
          "submissionStatus",
          "submissionType",
          "dueDate",
          "documents",
        ],
      },
      {
        name: "Location",
        fields: ["facilityId", "states", "generatorStatus"],
      },
      {
        name: "Tags",
        fields: ["facilityTags"],
      },
      {
        name: "Assignment",
        fields: ["reviewers", "signer", "staffAssignee", "waitingOn"],
      },
    ],
    []
  );

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        id="submission-filter-header"
        paddingX={2}
        paddingY={1.5}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1.5}
          flexWrap="wrap"
        >
          <Stack direction="row" gap={1.5} alignItems="flex-end" flexWrap="wrap">
            {/* Status Filter */}
            <FormControl variant="standard" size="small" sx={{ width: 140 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                open={statusOpen}
                onOpen={() => setStatusOpen(true)}
                onClose={() => setStatusOpen(false)}
                multiple
                value={selectedStatuses}
                onChange={handleStatusesChange}
                label="Status"
                variant="standard"
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  "data-1p-ignore": "true",
                }}
                sx={{
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                renderValue={(selected) => {
                  if (selected.length === ALL_STATUSES.length) {
                    return `All (${selected.length})`;
                  }
                  if (selected.length <= 2) {
                    return (selected as string[])
                      .map(
                        (s) => STATUS_FILTER_OPTIONS[s as SubmissionStatus] || s
                      )
                      .join(", ");
                  }
                  return `${selected.length} selected`;
                }}
                MenuProps={{
                  MenuListProps: {
                    autoFocusItem:
                      selectedStatuses.length === ALL_STATUSES.length
                        ? false
                        : true,
                  },
                }}
              >
                <MenuItem value="all" onClick={handleToggleAllStatuses}>
                  <Checkbox
                    checked={selectedStatuses.length === ALL_STATUSES.length}
                  />
                  Select All
                </MenuItem>
                {Object.entries(STATUS_FILTER_OPTIONS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    <Checkbox checked={selectedStatuses.includes(key)} />
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Generator Status Filter */}
            <FormControl variant="standard" size="small" sx={{ width: 110 }}>
              <InputLabel id="generator-status-filter-label">
                Gen Status
              </InputLabel>
              <Select
                labelId="generator-status-filter-label"
                open={generatorStatusOpen}
                onOpen={() => setGeneratorStatusOpen(true)}
                onClose={() => setGeneratorStatusOpen(false)}
                multiple
                value={selectedGeneratorStatuses}
                onChange={handleGeneratorStatusChange}
                label="Gen Status"
                variant="standard"
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  "data-1p-ignore": "true",
                }}
                sx={{
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                renderValue={(selected) => {
                  if (selected.length === ALL_GENERATOR_STATUSES.length) {
                    return `All (${selected.length})`;
                  }
                  return selected
                    .map(
                      (s) =>
                        GENERATOR_STATUS_OPTIONS.find((o) => o.value === s)
                          ?.label ?? s
                    )
                    .join(", ");
                }}
              >
                <MenuItem
                  value="select-all"
                  onClick={handleToggleAllGeneratorStatuses}
                >
                  <Checkbox
                    checked={
                      selectedGeneratorStatuses.length ===
                      ALL_GENERATOR_STATUSES.length
                    }
                    indeterminate={
                      selectedGeneratorStatuses.length > 0 &&
                      selectedGeneratorStatuses.length <
                        ALL_GENERATOR_STATUSES.length
                    }
                  />
                  Select All
                </MenuItem>
                {GENERATOR_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={selectedGeneratorStatuses.includes(option.value)}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* State Filter */}
            <FormControl variant="standard" size="small" sx={{ width: 100 }}>
              <InputLabel id="state-filter-label">State</InputLabel>
              <Select
                labelId="state-filter-label"
                open={stateOpen}
                onOpen={() => setStateOpen(true)}
                onClose={() => setStateOpen(false)}
                multiple
                value={selectedStates}
                onChange={handleStatesChange}
                label="State"
                variant="standard"
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  "data-1p-ignore": "true",
                }}
                sx={{
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "None";
                  }
                  if (selected.length === US_STATES.length) {
                    return `All (${selected.length})`;
                  }
                  if (selected.length <= 3) {
                    return (selected as string[]).join(", ");
                  }
                  return `${selected.length} selected`;
                }}
                MenuProps={{
                  MenuListProps: {
                    autoFocusItem:
                      selectedStates.length === US_STATES.length ? false : true,
                  },
                }}
              >
                <MenuItem value="select-all" onClick={handleToggleAllStates}>
                  <Checkbox
                    checked={selectedStates.length === US_STATES.length}
                    indeterminate={
                      selectedStates.length > 0 &&
                      selectedStates.length < US_STATES.length
                    }
                  />
                  Select All
                </MenuItem>
                {US_STATES.map((state) => (
                  <MenuItem key={state} value={state}>
                    <Checkbox checked={selectedStates.includes(state)} />
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Due Date Range Filter */}
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker
                value={[dueDateStart, dueDateEnd]}
                onChange={handleDueDateRangeChange}
                localeText={{ start: "Due Start", end: "Due End" }}
                slotProps={{
                  shortcuts: {
                    items: datePickerShortcuts,
                  },
                  textField: {
                    variant: "standard",
                    size: "small",
                    sx: { width: 105 },
                    inputProps: {
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                      "data-1p-ignore": "true",
                    },
                  },
                }}
                slots={{
                  field: MultiInputDateRangeField,
                }}
              />
            </LocalizationProvider>

            {/* Tags and Metric Chips */}
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              sx={{ alignSelf: "flex-end", mb: 0.5 }}
            >
              {/* Tags Filter */}
              <Box>
                <Chip
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>
                        {selectedFacilityTagNames.length > 0
                          ? `Tags (${selectedFacilityTagNames.length})`
                          : "Tags"}
                      </span>
                      <ExpandMoreIcon fontSize="small" />
                    </Stack>
                  }
                  color={tagsMenuAnchor ? "primary" : "default"}
                  variant="filled"
                  onClick={(e) => setTagsMenuAnchor(e.currentTarget)}
                  clickable
                  size="small"
                />
                <Menu
                  anchorEl={tagsMenuAnchor}
                  open={Boolean(tagsMenuAnchor)}
                  onClose={() => setTagsMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  slotProps={{
                    paper: { sx: { maxHeight: "50vh", py: 0 } },
                    list: { sx: { p: 0 } },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: 220,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="subtitle2">Facility Tags</Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setFacilityTagsExpanded(!facilityTagsExpanded)
                        }
                      >
                        {facilityTagsExpanded ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Box>
                    <Divider />
                    {facilityTagsExpanded && (
                      <Box
                        sx={{ maxHeight: 250, px: 1, overflowY: "auto" }}
                        onScroll={handleFacilityTagsScroll}
                      >
                        {allFacilityTags.length === 0 && (
                          <MenuItem disabled>No tags available</MenuItem>
                        )}
                        {allFacilityTags.length > 0 && (
                          <MenuItem
                            sx={{ p: 0 }}
                            onClick={() => {
                              const isAllSelected =
                                selectedFacilityTagNames.length ===
                                allFacilityTags.length;
                              if (isAllSelected) {
                                // Deselect all - remove from omnisearch
                                removeOmnisearchKeyValue("facilityTags");
                              } else {
                                // Select all - add all tag names to omnisearch
                                // Quote tag names that contain spaces
                                const tagNamesString = allFacilityTags
                                  .map((t) =>
                                    t.name.includes(" ")
                                      ? `"${t.name}"`
                                      : t.name
                                  )
                                  .join(",");
                                editOmnisearchKeyValues([
                                  ["facilityTags", tagNamesString],
                                ]);
                              }
                            }}
                          >
                            <Checkbox
                              checked={
                                selectedFacilityTagNames.length ===
                                allFacilityTags.length
                              }
                              indeterminate={
                                selectedFacilityTagNames.length > 0 &&
                                selectedFacilityTagNames.length <
                                  allFacilityTags.length
                              }
                            />
                            Select All
                          </MenuItem>
                        )}
                        {allFacilityTags.map((tag) => (
                          <MenuItem
                            key={tag.id}
                            sx={{ p: 0 }}
                            onClick={() => {
                              const isSelected = selectedFacilityTagNames.some(
                                (name) =>
                                  name.toLowerCase() === tag.name.toLowerCase()
                              );
                              const newNames = isSelected
                                ? selectedFacilityTagNames.filter(
                                    (name) =>
                                      name.toLowerCase() !==
                                      tag.name.toLowerCase()
                                  )
                                : [...selectedFacilityTagNames, tag.name];
                              if (newNames.length === 0) {
                                removeOmnisearchKeyValue("facilityTags");
                              } else {
                                // Quote tag names that contain spaces
                                const tagNamesString = newNames
                                  .map((name) =>
                                    name.includes(" ") ? `"${name}"` : name
                                  )
                                  .join(",");
                                editOmnisearchKeyValues([
                                  ["facilityTags", tagNamesString],
                                ]);
                              }
                            }}
                          >
                            <Checkbox
                              checked={selectedFacilityTagNames.some(
                                (name) =>
                                  name.toLowerCase() === tag.name.toLowerCase()
                              )}
                            />
                            <Tooltip title={tag.name} placement="right">
                              <Typography
                                noWrap
                                sx={{
                                  maxWidth: 160,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {tag.name}
                              </Typography>
                            </Tooltip>
                          </MenuItem>
                        ))}
                        {isFetchingMoreFacilityTags && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              py: 1,
                            }}
                          >
                            <CircularProgress size={20} />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Menu>
              </Box>
              {/* Ready for Action toggle chip */}
              <Suspense
                fallback={
                  <Chip label="Ready: —" size="small" variant="outlined" />
                }
              >
                <MetricChips
                  filter={statsFilter}
                  isReadyActive={readyForAction}
                  onReadyToggle={handleReadyForActionToggle}
                />
              </Suspense>
            </Stack>
          </Stack>
          <Stack direction="row" gap={0.5} alignItems="center">
            <Button onClick={handleResetFilters} variant="text" size="small">
              Default Filters
            </Button>
            <Button onClick={handleToggle} variant="text" size="small">
              {statsOpen ? "Hide Stats" : "Stats"}
            </Button>
            {isStaff && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSubmission}
                color="secondary"
              >
                Create Submission
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
      <SubmissionStatsOverview
        isOpen={statsOpen}
        filter={statsFilter}
        onStatusClick={handleChartStatusClick}
        selectedStatus={selectedChartStatus}
        onStateClick={handleChartStateClick}
        selectedState={selectedChartState}
        onGeneratorStatusClick={handleChartGeneratorStatusClick}
        selectedGeneratorStatus={selectedChartGeneratorStatus}
      />
      <Stack
        id="submission-list"
        p={2}
        sx={{
          background: theme.palette.grey50.main,
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            pb: 3,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h5"
                  color="text.primary"
                  pb={1}
                >
                  {titleFilter}
                </Typography>
                <Typography fontSize={12} component="p">
                  General waste reporting submissions. Use the search and
                  filters to find specific submissions.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box>
            <OmnisearchDataGrid
              slots={{ noRowsOverlay: CustomNoRowsOverlay }}
              hideEmptyOverlayWhileLoading
              persistenceKey={omnisearchPersistenceKey}
              isURLDriven
              columns={omnisearchColumns}
              dataQuery={SUBMISSION_VIRTUAL_EVENTS_QUERY}
              defaultSearch={defaultSearch}
              getItems={(data) => data.submissionVirtualEvents.items}
              getCount={(data) => data.submissionVirtualEvents.count ?? 0}
              checkboxSelection={isStaff}
              selectedIds={isStaff ? selectedIds : undefined}
              onSelectedRowsChanged={
                isStaff ? (rows) => setSelectedRows(rows) : undefined
              }
              onSelectedIdsChanged={
                isStaff ? (ids) => setSelectedIds(ids as string[]) : undefined
              }
              onRowClick={({ row }) => handleRowClick(row)}
              commandButtons={
                isStaff
                  ? [
                      <SubmissionBulkActionsMenu
                        key="bulk-actions"
                        submissions={selectedRows}
                        onClearSelections={() => {
                          setSelectedRows([]);
                          setSelectedIds([]);
                        }}
                      />,
                    ]
                  : []
              }
              initialPageSize={50}
              rowHeight={"auto"}
              columnVisibilityModel={{
                notes: false,
                waitingOn: false,
                ...(isMobile && {
                  facilities: false,
                  tags: false,
                }),
              }}
              additionalFilterColumns={[
                {
                  header: "Status",
                  key: "submissionStatus",
                  filterKeyType: "enumAutocomplete",
                  enumValues: ALL_STATUSES,
                  enumPresentationFunction: (value: string) =>
                    STATUS_FILTER_OPTIONS[value as SubmissionStatus] || value,
                },
                {
                  header: "State",
                  key: "states",
                  filterKeyType: "enumAutocomplete",
                  enumValues: US_STATES,
                  enumPresentationFunction: (value: string) =>
                    STATE_NAMES[value as keyof typeof STATE_NAMES] || value,
                },
                {
                  header: "Generator Status",
                  key: "generatorStatus",
                  filterKeyType: "enumAutocomplete",
                  enumValues: ALL_GENERATOR_STATUSES,
                  enumPresentationFunction: (value: string) =>
                    GENERATOR_STATUS_OPTIONS.find((o) => o.value === value)
                      ?.label ?? value,
                },
                {
                  header: "Tags",
                  key: "facilityTags",
                  filterKeyType: "facilityTags",
                },
                {
                  header: "Facility",
                  key: "facilityId",
                  filterKeyType: "multipleFacility",
                },
                {
                  header: "Attachment Name",
                  key: "documents",
                  filterKeyType: "text",
                },
                {
                  header: "Submission Type",
                  key: "submissionType",
                  filterKeyType: "text",
                },
                {
                  header: "Due Date",
                  key: "dueDate",
                  filterKeyType: "date",
                  dateRangeShortcuts: true,
                },
                {
                  header: "Reviewer",
                  key: "reviewers",
                  filterKeyType: "text",
                },
                {
                  header: "Signer",
                  key: "signer",
                  filterKeyType: "text",
                },
                {
                  header: "Encamp Assignee",
                  key: "staffAssignee",
                  filterKeyType: "text",
                },
                {
                  header: "Waiting On",
                  key: "waitingOn",
                  filterKeyType: "multipleUser",
                },
              ]}
              excludeFilterColumns={[
                "submissionType",
                "dueDate",
                "status",
                "documents",
                "reviewers",
                "signer",
                "staffAssignee",
                "facilityName",
                "title",
                "facilityId",
                "deadline",
                "facilities",
                "submission.status",
                "waitingOn",
              ]}
              filterGroups={filterGroups}
              tenantId={tenantId}
            />
          </Box>
        </Paper>
      </Stack>
    </Stack>
  );
}
